import * as z from 'zod';
import { IdSchema, TimestampSchema } from './horde-schema-primitives';

export const ResupplyPurchaseRecordSchema = z.object({
    completed: z.boolean(),
    cost: z.number().int().nonnegative(),
    id: IdSchema,
    purchaseId: IdSchema,
    round: z.number().int().positive(),
    targetRefs: z.array(IdSchema),
});
export type ResupplyPurchaseRecord = z.infer<typeof ResupplyPurchaseRecordSchema>;

export const PublicSecretStateSchema = z.object({
    cardId: IdSchema.nullable(),
    revealed: z.boolean(),
    selected: z.boolean(),
    status: z.enum(['hidden', 'revealed', 'completed', 'failed']),
});
export type PublicSecretState = z.infer<typeof PublicSecretStateSchema>;

export const PublicPlayerStateSchema = z.object({
    cp: z.number().int().nonnegative(),
    displayName: z.string().min(1),
    factionId: IdSchema,
    id: IdSchema,
    pointsShare: z.number().int().nonnegative(),
    publicSecret: PublicSecretStateSchema,
    purchasesThisTurn: z.array(ResupplyPurchaseRecordSchema),
    savedRewardCardIds: z.array(IdSchema),
    sp: z.number().int().nonnegative(),
    survival: z.enum(['unknown', 'surviving', 'eliminated']),
    warlordDeclared: z.boolean(),
});
export type PublicPlayerState = z.infer<typeof PublicPlayerStateSchema>;

export const AuthoritativePlayerSecretStateSchema = z.object({
    completed: z.boolean().nullable(),
    heldCardId: IdSchema.nullable(),
    offeredCardIds: z.array(IdSchema),
    privateActionIds: z.array(IdSchema),
    privateTallies: z.record(z.string(), z.number()),
    revealed: z.boolean(),
});
export type AuthoritativePlayerSecretState = z.infer<typeof AuthoritativePlayerSecretStateSchema>;

export const HiddenDeckStateSchema = z.object({
    deckId: z.enum(['misery', 'secondary', 'secretObjective']),
    discardPile: z.array(IdSchema),
    drawPile: z.array(IdSchema),
    shuffleRevision: z.number().int().nonnegative(),
});
export type HiddenDeckState = z.infer<typeof HiddenDeckStateSchema>;

export const MaterializedRandomResultSchema = z.discriminatedUnion('kind', [
    z
        .object({
            createdAt: TimestampSchema,
            id: IdSchema,
            kind: z.literal('dice'),
            purpose: z.string().min(1).max(256),
            result: z.union([z.number().int(), z.array(z.number().int())]),
        })
        .strict(),
    z
        .object({
            createdAt: TimestampSchema,
            id: IdSchema,
            kind: z.literal('shuffle'),
            purpose: z.string().min(1).max(256),
            result: z
                .object({
                    cardIds: z.array(IdSchema).default([]),
                    count: z.number().int().nonnegative().optional(),
                })
                .strict(),
        })
        .strict(),
    z
        .object({
            createdAt: TimestampSchema,
            id: IdSchema,
            kind: z.union([z.literal('choice'), z.literal('tieBreak')]),
            purpose: z.string().min(1).max(256),
            result: IdSchema,
        })
        .strict(),
]);
export type MaterializedRandomResult = z.infer<typeof MaterializedRandomResultSchema>;

export const HostSecretVaultStateSchema = z
    .object({
        gameId: IdSchema,
        hiddenDecks: z.array(HiddenDeckStateSchema),
        hostDeviceId: IdSchema,
        players: z.record(IdSchema, AuthoritativePlayerSecretStateSchema),
        randomResults: z.array(MaterializedRandomResultSchema),
        revision: z.number().int().nonnegative(),
    })
    .strict();
export type HostSecretVaultState = z.infer<typeof HostSecretVaultStateSchema>;

export const PlayerSecretViewStateSchema = z.object({
    completed: z.boolean().nullable(),
    gameId: IdSchema,
    heldCardId: IdSchema.nullable(),
    offeredCardIds: z.array(IdSchema),
    playerId: IdSchema,
    privateActionIds: z.array(IdSchema),
    privateTallies: z.record(z.string(), z.number()),
    revealed: z.boolean(),
    revision: z.number().int().nonnegative(),
    sourceVaultRevision: z.number().int().nonnegative(),
});
export type PlayerSecretViewState = z.infer<typeof PlayerSecretViewStateSchema>;
